// Implements: F-IDN-027
/**
 * The Personas table (v3 `vPeople`): person, role, teams and a row menu. The "Último acceso" and
 * "Mockups" columns of v3 are left out: the server has no such data yet (Default P3-5).
 *
 * The caller's own row has no menu and a disabled role select; the organization's only admin
 * cannot be removed (Default P3-4), so that row's "Quitar" is disabled. Each team badge links to
 * its team, marked "· líder" when the person leads it.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { OrgRole, PersonView } from "../../../types/organization";
import type { PersonTeam } from "../model/people";
import styles from "./Organization.module.css";
import { PersonCell } from "./PersonCell";
import { RoleSelect } from "./RoleSelect";

export interface PeopleRow {
  readonly person: PersonView;
  readonly teams: readonly PersonTeam[];
}

export interface PeopleTableProps {
  readonly rows: readonly PeopleRow[];
  readonly currentUserId: string;
  /** The person who is the organization's only admin, or `null` when there are several or none. */
  readonly soleAdminId: string | null;
  readonly teamHref: (teamId: string) => string;
  readonly onRoleChange: (person: PersonView, role: OrgRole) => void;
  readonly onRemove: (person: PersonView) => void;
}

export function PeopleTable({
  rows,
  currentUserId,
  soleAdminId,
  teamHref,
  onRoleChange,
  onRemove,
}: PeopleTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t("organization.people.column.person")}</th>
            <th scope="col">{t("organization.people.column.role")}</th>
            <th scope="col">{t("organization.people.column.teams")}</th>
            <th scope="col">
              <span className={styles.srOnly}>{t("organization.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ person, teams }) => {
            const own = person.user.id === currentUserId;
            const name = person.user.full_name;
            return (
              <tr key={person.user.id}>
                <td>
                  <PersonCell user={person.user} />
                </td>
                <td>
                  {/* A listing without roles (a team lead's, Default P3-2) has no role to change. */}
                  {person.role === undefined ? (
                    <span className={styles.meta}>{t("common.value.unknown")}</span>
                  ) : (
                    <RoleSelect
                      id={`person-role-${person.user.id}`}
                      personName={name}
                      value={person.role}
                      own={own}
                      onChange={(role) => {
                        onRoleChange(person, role);
                      }}
                    />
                  )}
                </td>
                <td>
                  {teams.length === 0 ? (
                    <span className={styles.meta}>{t("organization.people.noTeam")}</span>
                  ) : (
                    <span className={styles.badges}>
                      {teams.map((team) => (
                        <Link key={team.id} to={teamHref(team.id)} className={styles.badgeLink}>
                          <Badge tone="outline">
                            {team.lead ? `${team.name} ${t("organization.people.leadSuffix")}` : team.name}
                          </Badge>
                        </Link>
                      ))}
                    </span>
                  )}
                </td>
                <td>
                  {own ? null : (
                    <div className={styles.actions}>
                      <Menu
                        label={t("organization.people.menuFor", { name })}
                        align="end"
                        entries={[
                          {
                            kind: "item",
                            id: "remove",
                            label: t("organization.people.menu.remove"),
                            icon: "user-minus",
                            danger: true,
                            disabled: person.user.id === soleAdminId,
                            onSelect: () => {
                              onRemove(person);
                            },
                          },
                        ]}
                        renderTrigger={(trigger) => (
                          <button
                            type="button"
                            {...trigger}
                            className={buttonClassName("ghost", "md", true)}
                            aria-label={t("organization.people.menuFor", { name })}
                          >
                            <Icon name="more" />
                          </button>
                        )}
                      />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
